'use client';

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { friendlyFormatIBAN, isValidIBAN } from 'ibantools';
import { useTranslation } from 'react-i18next';

import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import { cn } from '@kit/ui/utils';

import type { BankAccountData, BankAccountInputProps } from './types';

export const BankAccountInput = forwardRef<
  HTMLInputElement,
  BankAccountInputProps
>(
  (
    {
      value = '',
      onChange,
      onBlur,
      disabled = false,
      placeholder,
      className,
      label,
      hint,
      error,
      required = false,
      id,
      name,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const resolvedPlaceholder =
      placeholder ?? t('dancer:bankInformation.ibanInputPlaceholder');
    const [inputValue, setInputValue] = useState(value);
    const [isFocused, setIsFocused] = useState(false);

    // Sync local state when external value prop changes
    useEffect(() => {
      if (value !== inputValue && !isFocused) {
        setInputValue(value);
      }
    }, [value, inputValue, isFocused]);

    // Validate and format IBAN
    const validateIban = useCallback((iban: string): BankAccountData => {
      const trimmedIban = iban.replace(/\s+/g, '').toUpperCase();

      if (!trimmedIban) {
        return {
          iban: '',
          formattedIban: '',
          isValid: false,
        };
      }

      // Skip validation for masked IBANs (e.g., ****1234)
      if (trimmedIban.startsWith('****')) {
        return {
          iban: trimmedIban,
          formattedIban: trimmedIban,
          isValid: true, // Treat masked IBANs as valid
        };
      }

      try {
        const isValid = isValidIBAN(trimmedIban);
        const formattedIban = friendlyFormatIBAN(trimmedIban) || trimmedIban;

        let country, bankCode, accountNumber;

        if (isValid && trimmedIban.length >= 4) {
          country = trimmedIban.substring(0, 2);

          // Extract basic bank info from BBAN (simplified approach)
          const bbanStart = 4; // IBAN starts with country code (2 chars) + check digits (2 chars)
          const bban = trimmedIban.substring(bbanStart);

          // This is a simplified extraction - real implementation would need
          // detailed parsing based on each country's BBAN structure
          if (bban.length >= 8) {
            bankCode = bban.substring(0, 4);
            accountNumber = bban.substring(4);
          }
        }

        return {
          iban: trimmedIban,
          formattedIban,
          country,
          isValid,
          bankCode,
          accountNumber,
        };
      } catch {
        // Handle ibantools library failures gracefully
        return {
          iban: trimmedIban,
          formattedIban: trimmedIban,
          isValid: false,
        };
      }
    }, []);

    // Get current validation state
    const validationData = useMemo(
      () => validateIban(inputValue),
      [inputValue, validateIban],
    );

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setInputValue(newValue);

        const metadata = validateIban(newValue);
        onChange?.(metadata.iban, metadata);
      },
      [onChange, validateIban],
    );

    const handleFocus = useCallback(() => {
      setIsFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
      setIsFocused(false);
      onBlur?.();
    }, [onBlur]);

    const displayValue = isFocused
      ? inputValue
      : validationData.formattedIban || inputValue;
    const hasError = error || (inputValue && !validationData.isValid);
    const errorId = hasError ? `${id}-error` : undefined;
    const _descriptionId = validationData.country
      ? `${id}-description`
      : undefined;

    return (
      <div className="space-y-2">
        {label && (
          <Label htmlFor={id} className={cn(required && 'required')}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}

        <div className={cn('relative w-full', className)}>
          <Input
            ref={ref}
            type="text"
            id={id}
            name={name}
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={resolvedPlaceholder}
            className={cn(
              hasError && 'border-destructive focus-visible:ring-destructive',
              validationData.isValid &&
                inputValue &&
                'border-green-500 focus-visible:ring-green-500',
            )}
            aria-invalid={!!hasError}
            aria-describedby={errorId}
            autoComplete="off"
            spellCheck="false"
            inputMode="text"
          />
        </div>

        {hint && !hasError && (
          <p className="text-muted-foreground text-sm">{hint}</p>
        )}

        {hasError && (
          <p id={errorId} className="text-destructive text-sm" role="alert">
            {error || t('dancer:bankInformation.invalidIBAN')}
          </p>
        )}
      </div>
    );
  },
);

BankAccountInput.displayName = 'BankAccountInput';
